import { Button, Image } from 'components'
import React from 'react'
import styled from 'styled-components'
import { GLOBAL_COLORS } from 'theme/global-theme'
import StakingImg from 'assets/staking-logo.png'
import { DEFAULT_INDEX_DECIMALS } from 'app-constants'

export enum TypeInput {
  empty = 'empty',
  error = 'error',
  success = 'success',
}

type TypeControls = 'max'
interface CustomInputProps {
  type: TypeInput
  value: string
  setValue: React.Dispatch<React.SetStateAction<string>>
  decimalPattern?: number
  controlsButton?: TypeControls[] | TypeControls
  maxValue?: string
  withLogo?: boolean
  borderRadiusStyle?: 'all' | 'left' | 'right' | 'none'
}

interface MaxButton {
  active: boolean
}

type InputNumberProps = CustomInputProps & React.HTMLProps<HTMLInputElement>

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`

const InputTypeNumber = styled.input<Omit<CustomInputProps, 'setValue'>>`
  width: 100%;
  font-family: DM Sans;
  font-weight: 400;
  font-size: 15px;
  letter-spacing: -0.01em;
  padding: ${(props) =>
    `11px ${props.controlsButton ? '76' : '11'}px 11px ${props.withLogo ? 45 : 11}px`};
  color: ${(props) => props.theme.text1};
  background: ${({ theme }) => theme.widgetBG};
  outline: none;
  border: 1px solid ${GLOBAL_COLORS.secondGrey05};
  border-radius: ${({ borderRadiusStyle }) => {
    switch (borderRadiusStyle) {
      case 'none':
        return `0px`
      case 'all':
        return `8px`
      case 'left':
        return `0px 8px 8px 0px`
      case 'right':
        return `8px 0px 0px 8px`
    }
  }};
  text-overflow: ellipsis;
  ::placeholder {
    color: ${({ theme }) => theme.inputPlaceholderColor};
  }
  :focus {
    outline: none;
    border: 1px solid ${({ theme }) => theme.inputPlaceholderColor};
  }
`
const MaxButton = styled(Button)<MaxButton>`
  position: absolute;
  width: 45px;
  right: 0px;
  height: 32px;
  margin: 6px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  color: ${(props) => (props.active ? GLOBAL_COLORS.white : props.theme.text1)};
  border: 1px solid
    ${(props) => (props.active ? GLOBAL_COLORS.transparent : GLOBAL_COLORS.secondGrey05)};
  &:hover {
    outline: none;
    background-color: ${(props) =>
      props.active ? GLOBAL_COLORS.darkUltramarine : GLOBAL_COLORS.black};
    border: 1px solid
      ${(props) => (props.active ? GLOBAL_COLORS.transparent : GLOBAL_COLORS.secondGrey05)};
    color: ${GLOBAL_COLORS.white};
  }
`

const Logo = styled(Image)`
  width: 20px;
  height: 20px;
`

const LogoWrapper = styled.div`
  height: 100%;
  width: min-content;
  position: absolute;
  display: flex;
  align-items: center;
  margin-left: 10px;
`

export const InputNumber: React.FC<InputNumberProps> = ({
  type,
  value,
  setValue,
  onFocus,
  onBlur,
  controlsButton,
  maxValue,
  decimalPattern,
  withLogo = false,
  borderRadiusStyle = 'all',
}) => {
  const decimals = decimalPattern || DEFAULT_INDEX_DECIMALS
  const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group
  const pattern = `^[0-9]*[.,]?[0-9]{0,${decimals}}$` // enforce pattern matching in HTML pattern attribute
  const escapeRegExp = (string: string): string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string

  const enforcer = (target: HTMLInputElement) => {
    let nextUserInput = target.value.replace(/,/g, '.')

    const lastIndex = nextUserInput.lastIndexOf('.')

    if (lastIndex) {
      nextUserInput = nextUserInput.slice(0, lastIndex + 1 + decimals) //18=decimals
    }
    if (nextUserInput === '' || inputRegex.test(escapeRegExp(nextUserInput))) {
      setValue(nextUserInput)
    }
    if (nextUserInput === '.') {
      setValue('0.')
    }
    if (
      inputRegex.test(escapeRegExp(nextUserInput)) &&
      nextUserInput.length > 1 &&
      nextUserInput[0] === '0' &&
      nextUserInput[1] !== '.'
    ) {
      const newInput = nextUserInput[0] + '.' + nextUserInput.slice(1)
      if (inputRegex.test(escapeRegExp(newInput))) {
        setValue(newInput)
      }
    }
  }

  return (
    <Wrapper>
      {withLogo && (
        <LogoWrapper>
          <Logo src={StakingImg} />
        </LogoWrapper>
      )}
      <InputTypeNumber
        type={type}
        value={value}
        placeholder="0.00"
        withLogo={withLogo}
        onChange={({ target }) => enforcer(target)}
        onFocus={onFocus}
        onBlur={onBlur}
        decimalPattern={decimalPattern}
        pattern={pattern}
        controlsButton={controlsButton}
        borderRadiusStyle={borderRadiusStyle}
      />
      {controlsButton === 'max' && (
        <MaxButton
          active={value === maxValue}
          buttonType={value === maxValue ? 'primary' : 'light-secondary'}
          text={'MAX'}
          onClick={() => setValue(maxValue || '')}
          fontType={'small'}
        />
      )}
    </Wrapper>
  )
}
